import { TranslocoService } from '@ngneat/transloco';
import { LearnerFormUpdatePermission } from 'src/app/page-modules/resource/models';
import { EditorContent, ReviewEventType, UserPlaylistSubmissionSummary } from '../models';

export class ContentHelper {
  static isFrameMode(): boolean {
    return window.self !== window.top;
  }

  /** Extracts a plain-text summary (no HTML tags) from an EditorContent[] block list, joining non-empty paragraphs. */
  static extractPlainText(content: EditorContent[] | null | undefined): string {
    if (!Array.isArray(content)) return '';
    return content
      .filter((block) => block.type === 'PARAGRAPH' && typeof block.content === 'string')
      .map((block) => (block.content as string).replace(/<[^>]*>/g, ' ').trim())
      .filter((text) => text.length > 0)
      .join('\n');
  }

  /** Whether an EditorContent[] block list has any real content: non-empty paragraph text, or any non-paragraph block. */
  static hasNonEmptyContent(content: EditorContent[] | null | undefined): boolean {
    if (!Array.isArray(content)) return false;
    return content.some((block) => {
      if (block.type !== 'PARAGRAPH') return true;
      const text = typeof block.content === 'string' ? block.content.replace(/<[^>]*>/g, '').trim() : '';
      return text.length > 0;
    });
  }

  static convertTimeEstimate(timeInMinutes: number): string {
    const hours = Math.floor(timeInMinutes / 60);
    const minutes = timeInMinutes % 60;
    if (hours === 0 && minutes === 0) {
      return '';
    }
    return hours ? `${hours}h ${minutes} min` : `${minutes} min`;
  }

  static getOrgDiscoveryUrl() {
    return localStorage.getItem('orgDiscoveryUrl') ?? 'discovery';
  }

  static setOrgDiscoveryUrl(url?: string) {
    url = url ? url : 'discovery';
    return localStorage.setItem('orgDiscoveryUrl', `/${url}`);
  }

  // TODO: replace with learnerFormAnswer.updatePermission when switching to new forms
  // return true if form can be submitted
  static checkFormSubmissionStatus(userPlaylistSubmissionSummary: UserPlaylistSubmissionSummary): boolean {
    let canReSubmit = false;
    if (userPlaylistSubmissionSummary && userPlaylistSubmissionSummary.submissionMode === 'SINGLE') {
      if (userPlaylistSubmissionSummary.submittedOn === null) {
        canReSubmit = true;
        return canReSubmit;
      }
      if (
        userPlaylistSubmissionSummary.submittedOn &&
        userPlaylistSubmissionSummary.userReviewSummary?.status?.currentReviewEvent?.type === ReviewEventType.REQUIRES_RESUBMISSION
      ) {
        canReSubmit = true;
        return canReSubmit;
      }
    }
    return canReSubmit;
  }

  static formCanNotBeEditedReason(translocoService: TranslocoService, formUpdatePermission: LearnerFormUpdatePermission): string {
    if (formUpdatePermission?.canBeUpdated === false) {
      switch (formUpdatePermission.notPermittedReason) {
        case 'REVIEW_OPEN':
          return translocoService.translate('translations.formUpdateDisabledReasons.reviewOpen');
        case 'REVIEW_CLOSED':
          return translocoService.translate('translations.formUpdateDisabledReasons.reviewClosed');
        case 'DEADLINE_PASSED':
          return translocoService.translate('translations.formUpdateDisabledReasons.deadlinePassed');
        case 'FORM_ALREADY_SUBMITTED':
          return translocoService.translate('translations.formUpdateDisabledReasons.formAlreadySubmitted');
        case 'PLAYLIST_ALREADY_SUBMITTED':
          return translocoService.translate('translations.formUpdateDisabledReasons.playlistAlreadySubmitted');
        default:
          return '';
      }
    } else {
      return '';
    }
  }

  static shouldDisplayReviewersDialogOnFormSubmission(userPlaylistSubmissionSummary: UserPlaylistSubmissionSummary): boolean {
    if (this.isProgressiveSubmissionMode(userPlaylistSubmissionSummary)) {
      const reviewSummary = userPlaylistSubmissionSummary?.userReviewSummary;
      if (
        reviewSummary &&
        reviewSummary.reviewType !== 'AUTOMATIC' &&
        !reviewSummary.areReviewersFixed &&
        (!reviewSummary.status || !reviewSummary.status.open || reviewSummary.canSelectNewReviewers)
      ) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }

  static isProgressiveSubmissionMode(userPlaylistSubmissionSummary: UserPlaylistSubmissionSummary): boolean {
    return userPlaylistSubmissionSummary?.submissionMode === 'PROGRESSIVE';
  }

  static stripHtmlTags(htmlContent: string): string {
    return htmlContent.replace(/<[^>]*>?|\s+/gm, ' ');
  }

  static stripOnlyHtmlTags(htmlContent: string): string {
    return htmlContent.replace(/<[^>]*>/g, ' '); // Removes HTML tags
  }

  static escapeHtml(htmlString: string): string {
    return htmlString.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;');
  }

  static unescapeHtml(htmlString: string): string {
    return htmlString
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#039;/g, "'");
  }

  /** Returns the first non-empty line of text found in the editor content, truncated to `wordLimit` words. */
  static getTextPreviewFromEditorContent(content: EditorContent[], wordLimit = 8): string {
    if (!content?.length) {
      return '';
    }

    for (const block of content) {
      if (typeof block.content !== 'string') {
        continue;
      }

      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = block.content;
      const text = tempDiv.textContent?.trim();

      if (text) {
        const words = text.split(/\s+/);
        return words.length > wordLimit ? `${words.slice(0, wordLimit).join(' ')}…` : text;
      }
    }

    return '';
  }
}

export function canTriggerSearch(event: KeyboardEvent): boolean {
  /* eslint-disable */
  return event.which < 9 || event.which > 40 || event.which === 32;
  /* eslint-enable */
}
